import { Injectable } from '@angular/core';

import { overlayConfigFactory } from 'ngx-modialog-11';
import { Modal, BSModalContext } from 'ngx-modialog-11/plugins/bootstrap';
import * as ext from '@app/extensions';
import { Broadcaster } from '../../common/broadcaster';
import { BroadcasterKey } from '../../common/enum/broadcaster-key.enum';
import { PackageService } from '../../services/package/package.service';
import { FieldType } from '../../services/package/field/field-type.enum';
import { AppearanceDataType, AppearanceType } from '../../services/appearance/appearance.enum';
import { appearanceModel } from '../../services/appearance/appearance.model';
import { AppearanceComponent } from '../../dialogs/appearance/appearance.component';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class SignatureInitialService {
  private isChangeAppearance$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private models = [null, null];
  private title = 'Initials';

  constructor(
    private broadcaster: Broadcaster,
    private packageService: PackageService,
    private modal: Modal,
  ) {}

  isEmpty(type) {
    return this.models[type] === null;
  }

  isEmptyData(type) {
    if (!this.models[type]) {
      return true;
    }
    let result = true;

    if (this.models[type].type === AppearanceDataType.Type) {
      result = ext.onlySpacesOrEmpty(this.models[type].data);
    } else {
      result = !this.models[type].data;
    }
    return result;
  }

  // getTitle() {
  //
  //     return this.title;
  // };

  getModelType(type) {
    return this.models[type];
  }

  get(type) {
    return this.models[type];
  }

  set(type, data) {
    if (!type && !data) {
      return;
    }

    this.update_(type, data);
  }

  getFieldValue(type) {
    if (!this.models[type]) {
      return null;
    }

    const result = {};

    if (this.models[type].fileId) {
      result['id'] = this.models[type].fileId;

      if (this.models[type].hasOwnProperty('useStored')) {
        result['useStored'] = this.models[type].useStored;
      }
    } else {
      result['text'] = this.models[type].data ? this.models[type].data : 'INITIAL';
      result['fontfamily'] = this.models[type].styles && this.models[type].styles.font ? this.models[type].styles.font : 'Arial';
      result['color'] =
        this.models[type].styles && this.models[type].styles.color
          ? this.transformColor(this.models[type].styles.color)
          : this.transformColor('#404040FF');
    }

    return result;
  }

  private transformColor(color: string): string {
    const transformColor = color.slice(1, -2);
    return `#FF${transformColor}`;
  }

  remove(type) {
    if (this.models[type] === 'Initials') {
      this.packageService.clareFiledByType(FieldType.Enum.Initials);
    }
    this.models[type] = null;
  }

  setDefaultModelOfType(type) {
    const appearenceModel = this.preparedModel(type);

    if (!appearanceModel) {
      return;
    }

    if (appearenceModel.tabs && appearenceModel.tabs.length > 0) {
      this.models[type] = appearenceModel.tabs[AppearanceDataType.Type];
    }
  }

  update(type, callback?) {
    type === AppearanceType.Initial ? (this.title = FieldType.Enum.Initials) : (this.title = FieldType.Enum.Signature);

    return this.modal.open(
      AppearanceComponent,
      overlayConfigFactory(
        {
          title: this.title,
          appearanceType: type,
          data: this.models[type],
          prepareModel: this.preparedModel.bind(this),
          callback: (data, appearanceType) => {
            if (callback) {
              callback(data);
            }

            this.update_(appearanceType, data);
          },
        },
        BSModalContext,
      ),
    );
  }

  private preparedModel(type) {
    const model = JSON.parse(JSON.stringify(appearanceModel));

    if (!this.models[type]) {
      this.setDefaultValue(type, model);

      return model;
    }

    model.currentType = this.models[type].type;

    if (this.models[type].type === AppearanceDataType.Type) {
      model.tabs[AppearanceDataType.Type] = this.models[type];
    }

    return JSON.parse(JSON.stringify(model));
  }

  private setDefaultValue(type, model) {
    if (!this.packageService || !model) {
      return;
    }
    const defaultValue = this.defaultValue(type);

    if (defaultValue) {
      model.tabs[AppearanceDataType.Type].data = defaultValue;
      model.isDefaultValue = true;
    }
  }

  private defaultValue(type) {
    const recipient = this.packageService.recipient;

    if (!recipient) {
      return null;
    }
    let result = null;

    if (type === 0) {
      if (recipient.firstName || recipient.lastName) {
        result = (recipient.firstName + ' ' + recipient.lastName).trim();
      }
      if (!result) {
        result = recipient.email;
      }
    }

    if (type === 1) {
      if (recipient.firstName) {
        result = recipient.firstName.substr(0, 1);
      }

      if (recipient.lastName) {
        result += recipient.lastName.substr(0, 1);
      }

      if (!result && recipient.email) {
        result = recipient.email.substr(0, 4);
      }
    }

    return result;
  }

  private update_(type, data) {
    this.models[type] = data;
    this.broadcaster.broadcast(BroadcasterKey.SignatureUpdated);
  }

  public isChangeAppearance(): Observable<boolean> {
    return this.isChangeAppearance$.asObservable();
  }

  public setChangeAppearance(v: boolean): void {
    this.isChangeAppearance$.next(v);
  }
}
